/**
 * JWT Session Management Hook
 *
 * OPERATIONAL FLOW:
 * ================
 *
 * 1. DUAL TIMER SYSTEM:
 *    - Activity Timer: Tracks user inactivity (10 min timeout)
 *    - Token Timer: Manages JWT lifecycle (refresh at 9 min, expire at 10 min)
 *
 * 2. USER ACTIVITY TRACKING:
 *    - Monitors mouse, keyboard, touch events
 *    - Throttles updates to every 5 seconds (performance)
 *    - Resets activity timer on each interaction
 *    - Stores last activity timestamp for token refresh decisions
 *
 * 3. TOKEN REFRESH LOGIC (at 9 minutes):
 *    - ONLY refreshes if user was active in last 1 minute
 *    - Skips refresh for inactive users (security)
 *    - On success: Updates token, resets token timer
 *    - On failure: Logs out user immediately
 *
 * 4. LOGOUT CONDITIONS:
 *    - 10 minutes of user inactivity (activity timer)
 *    - Token refresh failure
 *    - No refresh function & token expired (10 min)
 *
 * 5. SPECIAL CASES:
 *    - Login page WITHOUT session-critical modal: Skip logout (user not authenticated)
 *    - Login page WITH session-critical modal: Force logout (security requirement)
 *    - Uses sessionModalTracker for checking active modals (no Redux dependency)
 *
 * 6. SESSION PERSISTENCE:
 *    - Survives component unmounts/navigation
 *    - Stores timer state in sessionStorage
 *    - Restores timers with remaining time on remount
 */

import { useEffect, useCallback, useState, useRef } from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ActionCreatorWithoutPayload } from "@reduxjs/toolkit";
import { Persistor } from "redux-persist";
import { QueryClient } from "react-query";
import { sessionModalTracker } from "../Utils/sessionModalTracker";
import { performLogoutCleanup } from "../services/standardizedLogout";

const ACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const TOKEN_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const TOKEN_REFRESH_THRESHOLD = 9 * 60 * 1000; // Refresh at 9 minutes
const ACTIVITY_THROTTLE_MS = 5000; // 5 seconds - limit timer resets
const ACTIVITY_EVENTS = ["mousedown", "keypress", "touchstart", "click"];

type TokenSelector<T = any> = (state: T) => { token: string };
type TokenRefreshFunction = () => Promise<void>;

export const useJwtExpiry = <T = any>(
  tokenSelector: TokenSelector<T>,
  resetAuthAction: ActionCreatorWithoutPayload<string>,
  refreshToken?: TokenRefreshFunction,
  persistor?: Persistor,
  queryClient?: QueryClient,
) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Track last active time
  const lastActiveTime = useRef<number>(Date.now());
  const lastActivityUpdate = useRef<number>(0);

  // Timers
  const activityTimer = useRef<NodeJS.Timeout | null>(null);
  const tokenTimer = useRef<NodeJS.Timeout | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useStore();
  const { token } = useSelector(tokenSelector);

  const handleLogout = useCallback(() => {
    // Prevent multiple simultaneous logout attempts
    if (isLoggingOut) {
      return;
    }

    // Check if on login page
    const currentPath = window.location.pathname;
    const isOnLoginPage = currentPath.includes("/login");

    // Check if any session-critical modal is active
    const hasActiveSessionModal = sessionModalTracker.hasActiveModals();

    // Skip logout if on login page and NO session-critical modal is open
    // This allows users to remain on login page without being redirected
    // but ensures logout when a session-critical modal is open (security requirement)
    if (isOnLoginPage && !hasActiveSessionModal) {
      return;
    }

    setIsLoggingOut(true);

    // Clear all timers
    if (activityTimer.current) clearTimeout(activityTimer.current);
    if (tokenTimer.current) clearTimeout(tokenTimer.current);

    // Show blocking alert for session expiry
    alert("Session expired. Please login again.");

    // Use standardized logout cleanup
    performLogoutCleanup({
      navigate,
      store,
      resetAuthAction,
      persistor,
      queryClient,
    });

    setTimeout(() => {
      setIsLoggingOut(false);
    }, 1000);
  }, [store, navigate, isLoggingOut, resetAuthAction, persistor, queryClient]);

  const resetActivityTimer = useCallback(() => {
    // Reset the activity timer - user will be logged out after 10 minutes of inactivity
    if (activityTimer.current) {
      clearTimeout(activityTimer.current);
    }

    activityTimer.current = setTimeout(() => {
      // Activity timeout reached - log out the user
      handleLogout();
    }, ACTIVITY_TIMEOUT);
  }, [handleLogout]);

  const resetTokenTimer = useCallback(
    (newToken?: string) => {
      if (tokenTimer.current) {
        clearTimeout(tokenTimer.current);
      }

      // Store the start time for this timer
      const startTime = Date.now();
      sessionStorage.setItem("hash", startTime.toString());

      // Store the current token in AuthPrev (before it gets refreshed)
      // Only update AuthPrev if we have a token and it's different from what's stored
      const currentAuthPrev = sessionStorage.getItem("AuthPrev");
      const currentToken = newToken || token;

      if (currentToken && currentToken !== "none") {
        // If this is a new token (after refresh), the old token should already be in AuthPrev
        // If this is initial setup, store the current token
        if (
          !currentAuthPrev ||
          (newToken && currentAuthPrev !== currentToken)
        ) {
          sessionStorage.setItem("AuthPrev", currentToken);
        }
      }

      const refreshTime = refreshToken
        ? TOKEN_REFRESH_THRESHOLD
        : TOKEN_TIMEOUT;

      // Set timer for token refresh/expiry
      tokenTimer.current = setTimeout(() => {
        // Check if user has been active in the last minute before refreshing
        // This prevents refreshing tokens for inactive users
        const timeSinceLastActivity = Date.now() - lastActiveTime.current;
        const oneMinute = 60 * 1000;
        const isUserActive = timeSinceLastActivity < oneMinute;

        if (refreshToken && !isRefreshing && !isLoggingOut && isUserActive) {
          // User is active - attempt token refresh
          setIsRefreshing(true);

          refreshToken()
            .then(() => {
              // Token refresh successful
              // Note: don't call resetTokenTimer here - the effect will handle it
              // when it detects the new token
            })
            .catch((error) => {
              // Token refresh failed - log out
              handleLogout();
            })
            .finally(() => {
              setIsRefreshing(false);
            });
        } else if (!refreshToken) {
          // If no refresh function provided, logout at token expiry
          handleLogout();
        }
        // If user is not active, do nothing - let activity timer handle logout
      }, refreshTime);
    },
    [refreshToken, isRefreshing, isLoggingOut, handleLogout, token],
  );

  const handleUserActivity = useCallback(() => {
    const now = Date.now();

    // Always update the activity time for accurate inactivity detection
    lastActiveTime.current = now;

    // But only reset timer if throttle period has passed to prevent excessive calls
    if (now - lastActivityUpdate.current >= ACTIVITY_THROTTLE_MS) {
      lastActivityUpdate.current = now;
      // User activity detected - reset activity timer (throttled)
      resetActivityTimer();
    }
  }, [resetActivityTimer]);

  // Initialize activity timer and listeners only once when hook mounts
  useEffect(() => {
    // Initialize timestamps
    lastActiveTime.current = Date.now();
    lastActivityUpdate.current = Date.now();

    // Set up activity timer (only once)
    resetActivityTimer();

    // Set up activity listeners
    ACTIVITY_EVENTS.forEach((eventType) => {
      document.addEventListener(eventType, handleUserActivity, {
        passive: true,
      });
    });

    // Cleanup function
    return () => {
      // Clear activity timer
      if (activityTimer.current) {
        clearTimeout(activityTimer.current);
      }

      // Remove activity listeners
      ACTIVITY_EVENTS.forEach((eventType) => {
        document.removeEventListener(eventType, handleUserActivity);
      });
    };
  }, []); // Empty dependency array - runs only once

  // Helper to trigger token refresh
  const triggerTokenRefresh = useCallback(() => {
    if (!refreshToken || isRefreshing || isLoggingOut) {
      if (!refreshToken) handleLogout();
      return;
    }

    setIsRefreshing(true);
    refreshToken()
      .catch((error) => {
        console.error("Token refresh failed:", error);
        handleLogout();
      })
      .finally(() => setIsRefreshing(false));
  }, [refreshToken, isRefreshing, isLoggingOut, handleLogout]);

  // Helper to restore timer with remaining time
  const restoreTimerWithRemainingTime = useCallback(
    (startTime: number) => {
      const elapsed = Date.now() - startTime;
      const timeout = refreshToken ? TOKEN_REFRESH_THRESHOLD : TOKEN_TIMEOUT;
      const remaining = timeout - elapsed;

      if (remaining > 0) {
        if (tokenTimer.current) clearTimeout(tokenTimer.current);
        tokenTimer.current = setTimeout(triggerTokenRefresh, remaining);
      } else {
        triggerTokenRefresh();
      }
    },
    [refreshToken, triggerTokenRefresh],
  );

  // Handle token timer - only initialize once or when token actually changes
  // Use sessionStorage to persist state across component remounts during navigation
  useEffect(() => {
    if (!token || token === "none") {
      // No valid token - clear timer and storage
      if (tokenTimer.current) clearTimeout(tokenTimer.current);
      sessionStorage.removeItem("AuthPrev");
      sessionStorage.removeItem("hash");
      return;
    }

    if (isRefreshing) {
      // Token is being refreshed - skip timer setup
      return;
    }

    const AuthPrev = sessionStorage.getItem("AuthPrev");
    const startTimeStr = sessionStorage.getItem("hash");
    const isNewToken = AuthPrev !== token;
    const hasExistingTimer = !!startTimeStr;

    // Case 1: First initialization or token changed (after refresh)
    if (!hasExistingTimer || isNewToken) {
      resetTokenTimer(token);
      return;
    }

    // Case 2: Hook remounted (navigation) - restore timer with remaining time
    restoreTimerWithRemainingTime(parseInt(startTimeStr, 10));
  }, [token, resetTokenTimer, isRefreshing, restoreTimerWithRemainingTime]);

  return {
    isRefreshing,
    isLoggingOut,
    lastActiveTime: lastActiveTime.current,
  };
};
